<div class="dashboard-container">
  <!-- Header -->
  <div class="dashboard-header">
    <div class="header-content">
      <h1>Tableau de Bord</h1>
      <p class="subtitle">{{ getCurrentDate() }} — {{ getScopeLabel() }}</p>
    </div>
    <div class="header-actions">
      <p-dropdown
        *ngIf="isAdmin"
        [options]="userOptions"
        [(ngModel)]="selectedUserId"
        (onChange)="onScopeChange()"
        optionLabel="label"
        optionValue="value"
        placeholder="Périmètre"
        styleClass="scope-dropdown">
      </p-dropdown>
      <button pButton type="button" label="Actualiser" icon="pi pi-refresh"
              class="p-button-outlined" (click)="refresh()"></button>
      <button pButton type="button" label="Nouvelle Comparaison" icon="pi pi-plus"
              (click)="navigateToComparison()"></button>
    </div>
  </div>

  <!-- Loading State -->
  <div *ngIf="loading" class="loading-state">
    <i class="pi pi-spin pi-spinner" style="font-size: 3rem;"></i>
    <p>Chargement des données...</p>
  </div>

  <!-- Error State -->
  <div *ngIf="error && !loading" class="error-state">
    <i class="pi pi-exclamation-triangle" style="font-size: 3rem; color: var(--red-500);"></i>
    <p>{{ error }}</p>
    <button pButton label="Réessayer" icon="pi pi-refresh" (click)="refresh()"></button>
  </div>

  <!-- Dashboard Content -->
  <div *ngIf="!loading && !error && overview" class="dashboard-content">

    <!-- KPIs Section -->
    <h2 class="section-title">Métriques Clés</h2>

    <div class="kpis-grid">
      <div class="kpi-card kpi-blue">
        <div class="kpi-content">
          <span class="kpi-label">Projets ce Mois</span>
          <span class="kpi-value">{{ overview.kpis.projects_this_month }}</span>
          <span class="kpi-change"
                [class.positive]="overview.kpis.evolution_projects > 0"
                [class.negative]="overview.kpis.evolution_projects < 0">
            {{ formatEvolution(overview.kpis.evolution_projects) }} vs mois dernier
          </span>
        </div>
      </div>

      <div class="kpi-card kpi-green">
        <div class="kpi-content">
          <span class="kpi-label">CA ce Mois</span>
          <span class="kpi-value">{{ formatAmount(overview.kpis.amount_this_month) }}</span>
          <span class="kpi-change"
                [class.positive]="overview.kpis.evolution_amount > 0"
                [class.negative]="overview.kpis.evolution_amount < 0">
            {{ formatEvolution(overview.kpis.evolution_amount) }} vs mois dernier
          </span>
        </div>
      </div>

      <div class="kpi-card kpi-amber">
        <div class="kpi-content">
          <span class="kpi-label">Revenu ce Mois</span>
          <span class="kpi-value">{{ formatAmount(overview.kpis.revenu_this_month) }}</span>
          <span class="kpi-change"
                [class.positive]="overview.kpis.evolution_revenu > 0"
                [class.negative]="overview.kpis.evolution_revenu < 0">
            {{ formatEvolution(overview.kpis.evolution_revenu) }} vs mois dernier
          </span>
        </div>
      </div>

      <div class="kpi-card kpi-violet">
        <div class="kpi-content">
          <span class="kpi-label">Taux de Conversion</span>
          <span class="kpi-value">{{ overview.kpis.conversion_rate.toFixed(1) }}%</span>
          <span class="kpi-subtitle">
            {{ overview.kpis.won }} gagnés / {{ overview.kpis.lost }} perdus
          </span>
        </div>
      </div>

      <div class="kpi-card kpi-pink">
        <div class="kpi-content">
          <span class="kpi-label">Devis Envoyés</span>
          <span class="kpi-value">{{ overview.kpis.devis_this_month }}</span>
          <span class="kpi-subtitle">{{ overview.kpis.devis_total }} au total</span>
        </div>
      </div>

      <div class="kpi-card kpi-cyan">
        <div class="kpi-content">
          <span class="kpi-label">Panier Moyen</span>
          <span class="kpi-value">{{ formatAmount(overview.kpis.average_basket) }}</span>
          <span class="kpi-subtitle">sur {{ overview.kpis.projects_total }} projets</span>
        </div>
      </div>

      <div class="kpi-card kpi-orange">
        <div class="kpi-content">
          <span class="kpi-label">Taux de Traitement</span>
          <span class="kpi-value">{{ overview.kpis.treatment_rate.toFixed(1) }}%</span>
          <span class="kpi-subtitle">
            <span class="status-treated">{{ overview.kpis.treated }} traités</span> ·
            <span class="status-pending">{{ overview.kpis.to_process }} à traiter</span>
          </span>
        </div>
      </div>

      <div class="kpi-card kpi-slate">
        <div class="kpi-content" *ngIf="overview.kpis.rank !== null; else activeProjects">
          <span class="kpi-label">Classement du Mois</span>
          <span class="kpi-value">#{{ overview.kpis.rank }}</span>
          <span class="kpi-subtitle">sur {{ overview.kpis.total_users }} commerciaux</span>
        </div>
        <ng-template #activeProjects>
          <div class="kpi-content">
            <span class="kpi-label">Dossiers Actifs</span>
            <span class="kpi-value">{{ overview.kpis.active_projects }}</span>
            <span class="kpi-subtitle">en cours dans le pipeline</span>
          </div>
        </ng-template>
      </div>
    </div>

    <!-- Évolution mensuelle -->
    <div class="stats-section">
      <h2 class="section-title">Activité sur 12 Mois</h2>
      <div class="stat-card chart-card-full">
        <div class="chart-container-lg">
          <canvas id="monthlyChart"></canvas>
        </div>
        <p *ngIf="!overview.monthly_evolution.length" class="empty-chart">
          Aucune donnée sur la période.
        </p>
      </div>
    </div>

    <!-- Pipeline & Assureurs -->
    <div class="stats-section">
      <h2 class="section-title">Pipeline &amp; Partenaires</h2>
      <div class="charts-grid">
        <div class="stat-card">
          <div class="stat-header"><h3>Pipeline de Vente</h3></div>
          <div class="chart-container">
            <canvas id="pipelineChart"></canvas>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-header"><h3>Top Assureurs (contrats souscrits)</h3></div>
          <div class="chart-container">
            <canvas id="assureursChart"></canvas>
          </div>
          <p *ngIf="!overview.top_assureurs.length" class="empty-chart">
            Aucun contrat souscrit pour le moment.
          </p>
        </div>
      </div>
    </div>

    <!-- Répartitions -->
    <div class="stats-section">
      <h2 class="section-title">Répartition des Projets</h2>
      <div class="charts-grid">
        <div class="stat-card">
          <div class="stat-header"><h3>Par Origine (source des leads)</h3></div>
          <div class="chart-container">
            <canvas id="origineChart"></canvas>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-header"><h3>Par Objet du Prêt</h3></div>
          <div class="chart-container">
            <canvas id="objetChart"></canvas>
          </div>
        </div>
      </div>
    </div>

    <!-- Dossiers à relancer -->
    <div class="stats-section">
      <h2 class="section-title">Dossiers à Relancer</h2>
      <div class="stat-card">
        <p-table
          [value]="overview.relances"
          responsiveLayout="scroll"
          [tableStyle]="{ 'min-width': '52rem' }"
        >
          <ng-template pTemplate="header">
            <tr>
              <th>Référence</th>
              <th>Client</th>
              <th>Phase</th>
              <th>Jours dans la phase</th>
              <th>Appels</th>
              <th>Montant</th>
              <th>Devis envoyé le</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-item>
            <tr>
              <td>{{ item.reference }}</td>
              <td>{{ item.client }}</td>
              <td>
                <p-tag [value]="item.phase" [severity]="getPhaseSeverity(item.phase)"></p-tag>
              </td>
              <td>
                <p-tag [value]="item.days_in_phase + ' j'"
                       [severity]="getRelanceSeverity(item.days_in_phase)"></p-tag>
              </td>
              <td>{{ item.nb_appels }}</td>
              <td>{{ formatAmount(item.amount) }}</td>
              <td>{{ item.date_envoi_devis ? (item.date_envoi_devis | date:'dd/MM/yyyy') : '—' }}</td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr>
              <td colspan="7">Aucun dossier en attente de relance. 🎉</td>
            </tr>
          </ng-template>
        </p-table>
      </div>
    </div>

    <!-- Statistiques équipe (admin) -->
    <div class="stats-section" *ngIf="isAdmin">
      <h2 class="section-title">Performance de l'Équipe (Admin)</h2>

      <div class="stat-card">
        <div *ngIf="loadingTeam" class="admin-stats-loading">
          <i class="pi pi-spin pi-spinner"></i>
          <span>Chargement des statistiques équipe...</span>
        </div>

        <p-table
          *ngIf="!loadingTeam"
          [value]="teamStats"
          [paginator]="true"
          [rows]="8"
          [rowsPerPageOptions]="[8, 15, 30]"
          responsiveLayout="scroll"
          sortField="total_projects"
          [sortOrder]="-1"
          [tableStyle]="{ 'min-width': '72rem' }"
        >
          <ng-template pTemplate="header">
            <tr>
              <th pSortableColumn="name">Utilisateur <p-sortIcon field="name"></p-sortIcon></th>
              <th pSortableColumn="total_projects">Projets <p-sortIcon field="total_projects"></p-sortIcon></th>
              <th pSortableColumn="projects_this_month">Ce mois <p-sortIcon field="projects_this_month"></p-sortIcon></th>
              <th pSortableColumn="total_amount">CA total <p-sortIcon field="total_amount"></p-sortIcon></th>
              <th pSortableColumn="total_revenu">Revenu <p-sortIcon field="total_revenu"></p-sortIcon></th>
              <th pSortableColumn="devis_envoyes">Devis <p-sortIcon field="devis_envoyes"></p-sortIcon></th>
              <th pSortableColumn="conversion_rate">Conversion <p-sortIcon field="conversion_rate"></p-sortIcon></th>
              <th pSortableColumn="treatment_rate">Traitement <p-sortIcon field="treatment_rate"></p-sortIcon></th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-member>
            <tr [class.current-user-row]="member.is_current_user">
              <td>
                <div class="user-cell">
                  <span class="user-name">{{ member.name }}</span>
                  <span class="user-email">{{ member.email }}</span>
                </div>
              </td>
              <td>{{ member.total_projects }}</td>
              <td>{{ member.projects_this_month }}</td>
              <td>{{ formatAmount(member.total_amount) }}</td>
              <td>{{ formatAmount(member.total_revenu) }}</td>
              <td>{{ member.devis_envoyes }}</td>
              <td>
                <p-tag [value]="member.conversion_rate.toFixed(1) + '%' + ' (' + member.won + 'G/' + member.lost + 'P)'"
                       [severity]="getConversionSeverity(member.conversion_rate)"></p-tag>
              </td>
              <td>
                <span class="status-treated">{{ member.treated }}</span> /
                <span class="status-pending">{{ member.to_process }}</span>
                <span class="treatment-pct">({{ member.treatment_rate.toFixed(1) }}%)</span>
              </td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr>
              <td colspan="8">Aucune statistique disponible.</td>
            </tr>
          </ng-template>
        </p-table>
      </div>
    </div>

    <!-- Actions -->
    <div class="actions-section">
      <h2 class="section-title">Actions Rapides</h2>
      <div class="actions-grid">
        <button pButton type="button" label="Nouvelle Comparaison"
                icon="pi pi-plus" (click)="navigateToComparison()"></button>
        <button pButton type="button" label="Historique des Projets"
                icon="pi pi-list" class="p-button-outlined"
                (click)="navigateToHistory()"></button>
      </div>
    </div>

  </div>
</div>
